import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import {
  AdminQueryKeys,
  deleteEmailSender,
  listEmailSenders,
  upsertEmailSender,
  type AdminEmailSenderResponse,
  type UpsertEmailSenderBody,
} from '../api';
import { EmailAlertAdminTabs } from '../components/EmailAlertAdminTabs';

const EMPTY: UpsertEmailSenderBody = {
  fromAddress: '',
  displayName: '',
  replyTo: '',
  isActive: true,
  isDefault: false,
};

/** Admin page: manage email sender (FROM) identities. */
export function EmailSendersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: AdminQueryKeys.emailSenders(),
    queryFn: ({ signal }) => listEmailSenders(signal),
  });

  const [form, setForm] = useState<UpsertEmailSenderBody>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: AdminQueryKeys.emailSenders() });
  };

  const upsert = useMutation({
    mutationFn: upsertEmailSender,
    onSuccess: () => {
      setForm(EMPTY);
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'No se pudo guardar.'),
  });

  const remove = useMutation({
    mutationFn: deleteEmailSender,
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Alertas por email</h1>
        <p className="mt-1 text-sm text-slate-500">Direcciones de envío (FROM) verificadas.</p>
      </header>

      <EmailAlertAdminTabs />

      {isLoading ? (
        <div className="flex justify-center py-8"><LoadingSpinner /></div>
      ) : isError || data === undefined ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudieron cargar los remitentes.</div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">FROM</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Default</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="senders-rows">
              {data.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400">Sin remitentes configurados.</td></tr>
              ) : (
                data.map((s: AdminEmailSenderResponse) => (
                  <tr key={s.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{s.fromAddress}</td>
                    <td className="px-4 py-3 text-slate-700">{s.displayName}</td>
                    <td className="px-4 py-3">{s.isActive ? 'Activo' : 'Inactivo'}</td>
                    <td className="px-4 py-3">{s.isDefault ? '★' : ''}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => remove.mutate(s.id)}
                        disabled={remove.isPending}
                        className="text-xs font-medium text-rose-600 hover:underline disabled:opacity-50"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="card space-y-3 p-4">
        <h2 className="text-base font-semibold text-slate-900">Añadir remitente</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <input className="input" placeholder="FROM (alerts@notify.docusense.com)" value={form.fromAddress}
            onChange={(e) => setForm({ ...form, fromAddress: e.target.value })} data-testid="sender-from" />
          <input className="input" placeholder="Nombre visible" value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })} data-testid="sender-name" />
          <input className="input" placeholder="Reply-to (opcional)" value={form.replyTo ?? ''}
            onChange={(e) => setForm({ ...form, replyTo: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
            Por defecto
          </label>
        </div>
        {error !== null ? <p className="text-xs text-rose-700">{error}</p> : null}
        <button
          type="button"
          onClick={() => upsert.mutate({ ...form, replyTo: form.replyTo !== undefined && form.replyTo !== null && form.replyTo !== '' ? form.replyTo : null })}
          disabled={upsert.isPending || form.fromAddress.trim() === '' || form.displayName.trim() === ''}
          className="btn-primary"
          data-testid="sender-submit"
        >
          {upsert.isPending ? 'Guardando…' : 'Añadir remitente'}
        </button>
      </div>
    </div>
  );
}
